import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Container,
  Grid,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { DataGrid } from '@mui/x-data-grid';
import { Pagination } from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Event as EventIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Group as GroupIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  QrCode as QrCodeIcon,
} from "@mui/icons-material";
import MKBox from "../components/MKBox";
import MKTypography from "../components/MKTypography";
import MKButton from "../components/MKButton";
import USCFSectionsManager from "../components/USCFSectionsManager";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import ThemedDateTimePicker from "../components/ThemedDateTimePicker";
import API_BASE_URL from "../apiConfig";
import { calculateFee } from "../utils/feeCalculator";
import { getDataGridStyles } from "../styles/dataGridStyles";
import { useTheme } from "../ThemeContext";
import {
  PRIMARY_PINK,
  PRIMARY_PINK_DARK,
  SUCCESS_GREEN,
  WARNING_ORANGE,
  TRANSPARENT_PINK_05,
  TRANSPARENT_PINK_08,
  TRANSPARENT_PINK_1,
  TRANSPARENT_PINK_3,
  TRANSPARENT_PINK_5,
  BORDER_LIGHT,
} from "../constants";



function EditEventPage() {
  const { darkMode } = useTheme();
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [form, setForm] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [eventRequests, setEventRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [exportingCSV, setExportingCSV] = useState(false);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [showQrDialog, setShowQrDialog] = useState(false);
  const [rsvps, setRsvps] = useState([]);
  const [rsvpsLoading, setRsvpsLoading] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);

  // Get shared DataGrid styles
  const dataGridStyles = getDataGridStyles(darkMode);


  useEffect(() => {
    const fetchEvent = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/api/events/${eventId}`, {
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error("Event not found");
        }

        const eventData = await response.json();
        setEvent(eventData);

        // Fetch club details if we have club_id
        if (eventData.club_id) {
          const clubResponse = await fetch(`${API_BASE_URL}/api/clubs/${eventData.club_id}`, {
            credentials: "include",
          });
          if (clubResponse.ok) {
            const clubData = await clubResponse.json();
            setClub(clubData);
          }
        }

        // Initialize form with event data
        const hasLocationUrl = !!(eventData.location_url && eventData.location_display_name);
        setForm({
          title: eventData.title || "",
          description: eventData.description || "",
          start_time: dayjs(eventData.start_time),
          end_time: dayjs(eventData.end_time),
          location: hasLocationUrl ? "" : (eventData.location || ""),
          location_url: eventData.location_url || "",
          location_display_name: eventData.location_display_name || "",
          location_is_url: hasLocationUrl,
          registration_url: eventData.registration_url || "",
          startgg_event: eventData.startgg_event || false,
          slug: eventData.slug || "",
          embed_registration: eventData.embed_registration || false,
          iframe_data: eventData.iframe_data || "",
          ticket_price: eventData.ticket_price || "",
          ticket_currency: eventData.ticket_currency || "USD",
          ticket_available: eventData.ticket_available || false,
          ticket_quantity: eventData.ticket_quantity || "",
          ticket_description: eventData.ticket_description || "",
          rsvp_enabled: eventData.rsvp_enabled || false,
          rsvp_limit: eventData.rsvp_limit || "",
          rsvp_deadline: eventData.rsvp_deadline ? dayjs(eventData.rsvp_deadline) : null,
          private_event: eventData.private_event || false,
          hide_location_until_rsvp: eventData.hide_location_until_rsvp || false,
          hide_rsvp_list: eventData.hide_rsvp_list || false,
          chess_event: eventData.chess_event || false,
          dnd_event: eventData.dnd_event || false,
          uscf_event: eventData.uscf_event || false,
          uscf_ticket_price: eventData.uscf_ticket_price || ""
        });

        // Fetch event requests if this is a private event
        if (eventData.private_event) {
          fetchEventRequests(eventData.id);
        }

      } catch (error) {
        console.error("Error fetching event:", error);
        setError("Failed to load event details");
      } finally {
        setLoading(false);
      }
    };

    if (eventId) {
      fetchEvent();
    }
  }, [eventId]);

  useEffect(() => {
    // Fetch RSVPs if RSVP is enabled
    if (event?.rsvp_enabled) {
      fetchRSVPs();
    }
    // Fetch tickets if ticket sales are enabled
    if (event?.ticket_available) {
      fetchTickets();
    }
  }, [event?.rsvp_enabled, event?.ticket_available, eventId]);

  const fetchRSVPs = async () => {
    if (!eventId) return;
    
    setRsvpsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/events/${eventId}/attendees`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        // Filter to only RSVP type attendees
        const rsvpData = data.filter(attendee => attendee.rsvp_type === 'rsvp');
        setRsvps(rsvpData);
      }
    } catch (error) {
      console.error("Error fetching RSVPs:", error);
    } finally {
      setRsvpsLoading(false);
    }
  };

  const fetchTickets = async () => {
    if (!eventId) return;
    
    setTicketsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/events/${eventId}/attendees`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        // Filter to only ticket type attendees
        const ticketData = data.filter(attendee => attendee.rsvp_type !== 'rsvp' && attendee.ticket_quantity > 0);
        setTickets(ticketData);
      }
    } catch (error) {
      console.error("Error fetching tickets:", error);
    } finally {
      setTicketsLoading(false);
    }
  };

  const exportToCSV = () => {
    if (!event || rsvps.length === 0) return;
    
    setExportingCSV(true);
    try {
      // Define CSV headers
      const headers = ['Name', 'Email'];
      if (event.chess_event) {
        headers.push('First Name', 'Last Name', 'Rating', 'Rating Provider', 'USCF ID');
      }
      if (event.dnd_event) {
        headers.push('Campaigns', 'Favorite Classes', 'Experience Level');
      }
      headers.push('RSVP Date', 'Status');
      
      // Build CSV rows
      const rows = rsvps.map(rsvp => {
        const baseRow = [
          rsvp.display_name || 'N/A',
          rsvp.email || 'N/A',
        ];
        
        if (event.chess_event) {
          baseRow.push(
            rsvp.chess_registration?.first_name || 'N/A',
            rsvp.chess_registration?.last_name || 'N/A',
            rsvp.chess_registration?.rating || 'N/A',
            rsvp.chess_registration?.rating_provider || 'N/A',
            rsvp.chess_registration?.uscf_id || 'N/A'
          );
        }

        if (event.dnd_event) {
          baseRow.push(
            rsvp.dnd_registration?.campaigns || 'N/A',
            rsvp.dnd_registration?.favorite_classes || 'N/A',
            rsvp.dnd_registration?.experience_level || 'N/A'
          );
        }
        
        baseRow.push(
          new Date(rsvp.created_at).toLocaleString(),
          rsvp.attendee_status || 'confirmed'
        );
        
        return baseRow.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',');
      });
      
      // Combine headers and rows
      const csvContent = [headers.join(','), ...rows].join('\n');
      
      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `${event.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_rsvps_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setSuccess('RSVPs exported to CSV successfully');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      setError('Failed to export CSV');
    } finally {
      setExportingCSV(false);
    }
  };

  const exportTicketsToCSV = () => {
    if (!event || tickets.length === 0) return;
    
    setExportingCSV(true);
    try {
      // Define CSV headers
      const headers = ['Name', 'Email', 'Confirmation Code', 'Quantity', 'Price', 'Currency'];
      if (event.chess_event) {
        headers.push('First Name', 'Last Name', 'Rating', 'Rating Provider', 'USCF ID');
      }
      if (event.dnd_event) {
        headers.push('Campaigns', 'Favorite Classes', 'Experience Level');
      }
      headers.push('Purchase Date', 'Status');
      
      // Build CSV rows
      const rows = tickets.map(ticket => {
        const baseRow = [
          ticket.display_name || 'N/A',
          ticket.email || 'N/A',
          ticket.confirmation_code || 'N/A',
          ticket.ticket_quantity || 1,
          ticket.ticket_price || '0.00',
          ticket.ticket_currency || 'USD',
        ];
        
        if (event.chess_event) {
          baseRow.push(
            ticket.chess_registration?.first_name || 'N/A',
            ticket.chess_registration?.last_name || 'N/A',
            ticket.chess_registration?.rating || 'N/A',
            ticket.chess_registration?.rating_provider || 'N/A',
            ticket.chess_registration?.uscf_id || 'N/A'
          );
        }

        if (event.dnd_event) {
          baseRow.push(
            ticket.dnd_registration?.campaigns || 'N/A',
            ticket.dnd_registration?.favorite_classes || 'N/A',
            ticket.dnd_registration?.experience_level || 'N/A'
          );
        }
        
        baseRow.push(
          new Date(ticket.created_at).toLocaleString(),
          ticket.attendee_status || 'confirmed'
        );
        
        return baseRow.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',');
      });
      
      // Combine headers and rows
      const csvContent = [headers.join(','), ...rows].join('\n');
      
      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `${event.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_tickets_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setSuccess('Tickets exported to CSV successfully');
    } catch (error) {
      console.error('Error exporting tickets CSV:', error);
      setError('Failed to export tickets CSV');
    } finally {
      setExportingCSV(false);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSwitchChange = (field) => (event) => {
    const newValue = event.target.checked;
    
    
    // If enabling RSVP, disable ticket sales and USCF event
    if (field === 'rsvp_enabled' && newValue) {
      setForm(prev => {
        const updated = { ...prev, [field]: newValue, ticket_available: false, uscf_event: false };
        return updated;
      });
    }
    // If enabling ticket sales, disable RSVP, private event, location hiding, and USCF event
    else if (field === 'ticket_available' && newValue) {
      setForm(prev => {
        const updated = { ...prev, [field]: newValue, rsvp_enabled: false, private_event: false, hide_location_until_rsvp: false, uscf_event: false };
        return updated;
      });
    }
    // If enabling USCF event, disable RSVP, ticket sales, private event, and location hiding
    else if (field === 'uscf_event' && newValue) {
      setForm(prev => {
        const updated = { ...prev, [field]: newValue, rsvp_enabled: false, ticket_available: false, private_event: false, hide_location_until_rsvp: false };
        return updated;
      });
    }
    // If disabling Start.gg event, disable embed registration
    else if (field === 'startgg_event' && !newValue) {
      setForm(prev => {
        const updated = { ...prev, [field]: newValue, embed_registration: false };
        return updated;
      });
    }
    // If disabling embed registration, clear iframe data
    else if (field === 'embed_registration' && !newValue) {
      setForm(prev => {
        const updated = { ...prev, [field]: newValue, iframe_data: '' };
        return updated;
      });
    }
    // Default case - just update the field
    else {
      setForm(prev => {
        const updated = { ...prev, [field]: newValue };
        return updated;
      });
    }
  };

  const handleDateChange = (field, value) => {
    setForm({ ...form, [field]: value });
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setError('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
        return;
      }

      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }

      setSelectedFile(file);
      setError("");

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setFilePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    
    
    // Validation
    if (!form.title.trim()) {
      setError("Title is required");
      return;
    }
    
    if (!form.description.trim()) {
      setError("Description is required");
      return;
    }
    
    if (form.location_is_url) {
      if (!form.location_display_name.trim()) {
        setError("Location display name is required");
        return;
      }
      if (!form.location_url.trim()) {
        setError("Location URL is required");
        return;
      }
      // Basic URL validation
      try {
        new URL(form.location_url.trim());
      } catch {
        setError("Location URL must be a valid URL");
        return;
      }
    } else {
      if (!form.location.trim()) {
        setError("Location is required");
        return;
      }
    }
    
    if (!form.start_time) {
      setError("Start time is required");
      return;
    }
    
    if (!form.end_time) {
      setError("End time is required");
      return;
    }
    
    if (form.end_time <= form.start_time) {
      setError("End time must be after start time");
      return;
    }

    // Validate Start.gg specific fields
    if (form.startgg_event && !form.slug.trim()) {
      setError("Slug is required when Start.gg event is enabled");
      return;
    }

    if (form.embed_registration && !form.iframe_data.trim()) {
      setError("Iframe data is required when embed registration is enabled");
      return;
    }

    // Validate RSVP fields
    if (form.rsvp_enabled && form.rsvp_limit && parseInt(form.rsvp_limit) <= 0) {
      setError("RSVP limit must be greater than 0");
      return;
    }

    if (form.rsvp_enabled && form.rsvp_deadline && form.rsvp_deadline <= dayjs()) {
      setError("RSVP deadline must be in the future");
      return;
    }

    setSaving(true);
    
    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('title', form.title.trim());
      formData.append('description', form.description.trim());
      formData.append('start_time', form.start_time.toISOString());
      formData.append('end_time', form.end_time.toISOString());
      if (form.location_is_url) {
        formData.append('location', form.location_display_name.trim());
        formData.append('location_url', form.location_url.trim());
        formData.append('location_display_name', form.location_display_name.trim());
      } else {
        formData.append('location', form.location.trim());
        formData.append('location_url', '');
        formData.append('location_display_name', '');
      }
      formData.append('registration_url', form.registration_url.trim());
      formData.append('startgg_event', form.startgg_event);
      formData.append('slug', form.slug.trim());
      formData.append('embed_registration', form.embed_registration);
      formData.append('iframe_data', form.iframe_data.trim());
      formData.append('ticket_price', form.ticket_price);
      formData.append('ticket_currency', form.ticket_currency);
      formData.append('ticket_available', form.ticket_available);
      formData.append('ticket_quantity', form.ticket_quantity);
      formData.append('ticket_description', form.ticket_description.trim());
      formData.append('rsvp_enabled', form.rsvp_enabled);
      formData.append('rsvp_limit', form.rsvp_limit);
      if (form.rsvp_deadline) {
        formData.append('rsvp_deadline', form.rsvp_deadline.toISOString());
      }
      formData.append('private_event', form.private_event);
      formData.append('hide_location_until_rsvp', form.hide_location_until_rsvp);
      formData.append('hide_rsvp_list', form.hide_rsvp_list);
      formData.append('uscf_event', form.uscf_event);
      formData.append('uscf_ticket_price', form.uscf_ticket_price);
      
      if (selectedFile) {
        formData.append('flyer', selectedFile);
      }

      const response = await fetch(`${API_BASE_URL}/api/events/${eventId}`, {
        method: "PUT",
        credentials: "include",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update event");
      }

      const updatedEvent = await response.json();
      setEvent(updatedEvent);
      setSuccess("Event updated successfully!");
      
      // Clear file selection after successful update
      setSelectedFile(null);
      setFilePreview(null);
    } catch (error) {
      console.error("Update event error:", error);
      setError(error.message || "Failed to update event");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this event? This action cannot be undone.")) {
      return;
    }

    setDeleting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/events/${eventId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to delete event");
      }

      setSuccess("Event deleted successfully!");
      setTimeout(() => {
        navigate(`/clubs/${club?.id}/edit`);
      }, 1500);
    } catch (error) {
      console.error("Delete event error:", error);
      setError(error.message || "Failed to delete event");
    } finally {
      setDeleting(false);
    }
  };

  const handleBackToClub = () => {
    if (club?.id) {
      navigate(`/clubs/${club.id}/edit`);
    } else {
      navigate("/clubs");
    }
  };

  const handleExportSwissSys = async () => {
    setExportingCSV(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/events/${eventId}/swisssys.csv`, {
        method: "GET",
        credentials: "include",
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `${event?.title?.replace(/[^a-zA-Z0-9]/g, '_')}_swisssys.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setSuccess('SwissSys CSV exported successfully');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to export SwissSys CSV');
      }
    } catch (error) {
      console.error('Error exporting SwissSys CSV:', error);
      setError('Failed to export SwissSys CSV');
    } finally {
      setExportingCSV(false);
    }
  };

  const handleGenerateQRCode = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/events/${eventId}/qr-code`, {
        method: "GET",
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setQrCodeData(data);
        setShowQrDialog(true);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to generate QR code');
      }
    } catch (error) {
      console.error('Error generating QR code:', error);
      setError('Failed to generate QR code');
    }
  };

  const fetchEventRequests = async (eventId) => {
    try {
      setRequestsLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/events/${eventId}/requests`, {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to fetch event requests");
      }

      const requests = await response.json();
      setEventRequests(requests);
    } catch (error) {
      console.error("Error fetching event requests:", error);
      setError("Failed to load event requests");
    } finally {
      setRequestsLoading(false);
    }
  };

  const handleRequestAction = async (requestId, status) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/events/${eventId}/requests/${requestId}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          status,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update request");
      }

      // Refresh the requests list
      await fetchEventRequests(eventId);
      
      setSuccess(`Request ${status} successfully!`);
    } catch (error) {
      console.error("Error updating request:", error);
      setError(`Failed to ${status} request`);
    }
  };

  const handleRemoveRequest = async (requestId) => {
    if (!window.confirm("Are you sure you want to remove this approved request? This action cannot be undone.")) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/events/${eventId}/requests/${requestId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to remove request");
      }

      // Refresh the requests list
      await fetchEventRequests(eventId);
      
      setSuccess("Request removed successfully!");
    } catch (error) {
      console.error("Error removing request:", error);
      setError("Failed to remove request");
    }
  };

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  // Calculate pagination
  const totalPages = Math.ceil(eventRequests.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentRequests = eventRequests.slice(startIndex, endIndex);

    // Define DataGrid columns for event requests
  const eventRequestColumns = [
    {
      field: 'user',
      headerName: 'User',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {params.row.avatar_url && (
            <Box
              component="img"
              src={params.row.avatar_url}
              alt={params.row.display_name}
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                objectFit: 'cover'
              }}
            />
          )}
          <Typography variant="body2" fontWeight="bold">
            {params.row.display_name}
          </Typography>
        </Box>
      )
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.row.email}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            bgcolor: params.value === 'approved' ? SUCCESS_GREEN :
                     params.value === 'rejected' ? WARNING_ORANGE : PRIMARY_PINK,
            color: 'white',
            fontWeight: 'bold'
          }}
        />
      )
    },
    {
      field: 'created_at',
      headerName: 'Date',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          {new Date(params.value).toLocaleDateString()}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        params.row.status === 'pending' ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <MKButton
              variant="contained"
              size="small"
              startIcon={<CheckCircleIcon />}
              onClick={() => handleRequestAction(params.row.id, 'approved')}
              sx={{
                bgcolor: SUCCESS_GREEN,
                "&:hover": { bgcolor: "#2e7d32" }
              }}
            >
              Approve
            </MKButton>
            <MKButton
              variant="contained"
              size="small"
              startIcon={<CancelIcon />}
              onClick={() => handleRequestAction(params.row.id, 'rejected')}
              sx={{
                bgcolor: WARNING_ORANGE,
                "&:hover": { bgcolor: "#f57c00" }
              }}
            >
              Reject
            </MKButton>
          </Box>
        ) : params.row.status === 'approved' ? (
          <MKButton
            variant="outlined"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={() => handleRemoveRequest(params.row.id)}
            sx={{
              borderColor: WARNING_ORANGE,
              color: WARNING_ORANGE,
              "&:hover": {
                borderColor: "#f57c00",
                backgroundColor: "rgba(245, 124, 0, 0.04)"
              }
            }}
          >
            Remove
          </MKButton>
        ) : null
      )
    }
  ];

  if (loading) {
    return (
      <MKBox
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </MKBox>
    );
  }

  if (error && !event) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <MKButton
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToClub}
        >
          Back to Club
        </MKButton>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
             {/* Header */}
       <MKBox sx={{ mb: 3 }}>
         <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
           <MKButton
             variant="outlined"
             startIcon={<ArrowBackIcon />}
             onClick={handleBackToClub}
             sx={{ 
               color: darkMode ? '#ffffff' : '#000000',
               borderColor: darkMode ? '#ffffff' : '#000000',
               '&:hover': {
                 borderColor: darkMode ? '#ffffff' : '#000000',
                 backgroundColor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
               }
             }}
           >
             Back to {club?.name || "Club"}
           </MKButton>
           
           <MKButton
             variant="outlined"
             startIcon={<VisibilityIcon />}
             onClick={() => navigate(`/events/${eventId}`)}
             sx={{
               color: darkMode ? '#ffffff' : '#000000',
               borderColor: darkMode ? '#ffffff' : '#000000',
               '&:hover': {
                 borderColor: darkMode ? '#ffffff' : '#000000',
                 backgroundColor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
               }
             }}
           >
             View Event
           </MKButton>
         </Box>

        <Typography variant="h3" component="h1" gutterBottom sx={{ color: PRIMARY_PINK }}>
          <EventIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
          Edit Event: {event?.title}
        </Typography>

        
      </MKBox>

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Main Event Details */}
          <Grid item xs={12} md={7}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h5" gutterBottom sx={{ color: PRIMARY_PINK }}>
                  Event Details
                </Typography>
                
                <TextField
                  margin="dense"
                  required
                  fullWidth
                  id="title"
                  label="Event Title"
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  placeholder="Enter event title"
                  sx={{ mb: 2 }}
                />
                
                <TextField
                  margin="dense"
                  required
                  fullWidth
                  id="description"
                  label="Description"
                  name="description"
                  multiline
                  rows={3}
                  value={form.description}
                  onChange={handleChange}
                  placeholder="Describe your event"
                  sx={{ mb: 2 }}
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.location_is_url}
                      onChange={handleSwitchChange('location_is_url')}
                      color="primary"
                    />
                  }
                  label="Location is a URL"
                  sx={{ mb: 2 }}
                />
                
                {form.location_is_url ? (
                  <>
                    <TextField
                      margin="dense"
                      required
                      fullWidth
                      id="location_display_name"
                      label="Location Display Name"
                      name="location_display_name"
                      value={form.location_display_name}
                      onChange={handleChange}
                      placeholder="e.g., Tournament Venue, Online Event"
                      sx={{ mb: 1 }}
                    />
                    <TextField
                      margin="dense"
                      required
                      fullWidth
                      id="location_url"
                      label="Location URL"
                      name="location_url"
                      value={form.location_url}
                      onChange={handleChange}
                      placeholder="https://example.com/venue"
                      sx={{ mb: 2 }}
                    />
                  </>
                ) : (
                  <TextField
                    margin="dense"
                    required
                    fullWidth
                    id="location"
                    label="Location"
                    name="location"
                    value={form.location}
                    onChange={handleChange}
                    placeholder="Where is the event taking place?"
                    sx={{ mb: 2 }}
                  />
                )}
                
                <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                  <ThemedDateTimePicker
                    label="Start Time"
                    value={form.start_time}
                    onChange={(value) => handleDateChange("start_time", value)}
                    views={['year', 'month', 'day', 'hours', 'minutes']}
                    minDateTime={dayjs()}
                    required
                  />
                  <ThemedDateTimePicker
                    label="End Time"
                    value={form.end_time}
                    onChange={(value) => handleDateChange("end_time", value)}
                    views={['year', 'month', 'day', 'hours', 'minutes']}
                    minDateTime={form.start_time || dayjs()}
                    required
                  />
                </Box>
              </CardContent>
            </Card>

            {/* Event Flyer */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: PRIMARY_PINK }}>
                  Event Flyer
                </Typography>
                
                {event?.flyer_url && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Current flyer:
                    </Typography>
                    <Box
                      component="img"
                      src={event.flyer_url}
                      alt="Current event flyer"
                      sx={{
                        width: "100%",
                        maxWidth: 400,
                        height: "auto",
                        borderRadius: 1,
                        border: `1px solid ${BORDER_LIGHT}`,
                      }}
                    />
                  </Box>
                )}
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Upload new flyer image. Supported formats: JPEG, PNG, GIF, WebP (max 5MB)
                  </Typography>
                  
                  {!selectedFile ? (
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<CloudUploadIcon />}
                                      sx={{
                  border: '2px dashed #ccc',
                  borderColor: PRIMARY_PINK,
                  color: PRIMARY_PINK,
                  '&:hover': {
                    borderColor: PRIMARY_PINK_DARK,
                    backgroundColor: TRANSPARENT_PINK_05,
                    color: PRIMARY_PINK_DARK
                  }
                }}
                    >
                      Choose New Flyer Image
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleFileSelect}
                      />
                    </Button>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        component="img"
                        src={filePreview}
                        alt="New flyer preview"
                        sx={{
                          width: 100,
                          height: 100,
                          objectFit: 'cover',
                          borderRadius: 1,
                          border: '1px solid #ccc'
                        }}
                      />
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {selectedFile.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <IconButton
                            size="small"
                            onClick={handleRemoveFile}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={5}>
            {/* RSVP Section */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: PRIMARY_PINK }}>
                  RSVP Settings (Free Events)
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Enable RSVP to allow users to sign up for your free event. Each user can RSVP for 1 spot.
                </Typography>
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.rsvp_enabled}
                      onChange={handleSwitchChange("rsvp_enabled")}
                      name="rsvp_enabled"
                                               sx={{
                          '& .MuiSwitch-root': {
                            '& .MuiSwitch-switchBase': {
                              color: form.rsvp_enabled ? PRIMARY_PINK : '#ccc',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: PRIMARY_PINK,
                              '&:hover': {
                                backgroundColor: TRANSPARENT_PINK_08,
                              },
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: PRIMARY_PINK,
                            },
                            '& .MuiSwitch-track': {
                              backgroundColor: form.rsvp_enabled ? PRIMARY_PINK : '#ccc',
                            },
                            '& .MuiSwitch-switchBase.Mui-disabled': {
                              color: '#ccc !important',
                            },
                            '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
                              backgroundColor: '#ccc !important',
                            },
                          },
                        }}
                    />
                  }
                  label="Enable RSVP for Free Event"
                  sx={{ mb: 2 }}
                />

                {form.rsvp_enabled && (
                  <Box sx={{ ml: 2, mb: 2 }}>
                    <TextField
                      margin="dense"
                      fullWidth
                      id="rsvp_limit"
                      label="RSVP Limit (Optional)"
                      name="rsvp_limit"
                      type="number"
                      value={form.rsvp_limit}
                      onChange={handleChange}
                      placeholder="Leave empty for unlimited"
                      inputProps={{ min: 1 }}
                      helperText="Maximum number of people who can RSVP"
                      sx={{ mb: 2 }}
                    />

                    <ThemedDateTimePicker
                      label="RSVP Deadline (Optional)"
                      value={form.rsvp_deadline}
                      onChange={(value) => handleDateChange("rsvp_deadline", value)}
                      views={['year', 'month', 'day', 'hours', 'minutes']}
                      minDateTime={dayjs()}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Ticket Sales Section */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: PRIMARY_PINK }}>
                  Ticket Sales
                </Typography>
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.ticket_available}
                      onChange={handleSwitchChange("ticket_available")}
                      name="ticket_available"
                      // disabled={form.rsvp_enabled}
                      disabled={true}
                      sx={{
                        '& .MuiSwitch-root': {
                          '& .MuiSwitch-switchBase': {
                            color: form.ticket_available ? PRIMARY_PINK : '#ccc',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: PRIMARY_PINK,
                            '&:hover': {
                              backgroundColor: TRANSPARENT_PINK_08,
                            },
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: PRIMARY_PINK,
                          },
                          '& .MuiSwitch-track': {
                            backgroundColor: form.ticket_available ? PRIMARY_PINK : '#ccc',
                          },
                          '& .MuiSwitch-switchBase.Mui-disabled': {
                            color: '#ccc !important',
                          },
                          '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
                            backgroundColor: '#ccc !important',
                          },
                        },
                      }}
                    />
                  }
                  label="Enable Ticket Sales (Coming Soon)"
                  sx={{ mb: 2 }}
                />

                {form.ticket_available && (
                  <Box sx={{ ml: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                      <TextField
                        margin="dense"
                        required
                        fullWidth
                        id="ticket_price"
                        label="Ticket Price"
                        name="ticket_price"
                        type="number"
                        value={form.ticket_price}
                        onChange={handleChange}
                        placeholder="0.00"
                        inputProps={{ min: 0, step: 0.01 }}
                        sx={{ flex: 2 }}
                      />
                      <TextField
                        margin="dense"
                        fullWidth
                        id="ticket_currency"
                        label="Currency"
                        name="ticket_currency"
                        value={form.ticket_currency}
                        onChange={handleChange}
                        select
                        sx={{ flex: 1 }}
                      >
                        <MenuItem value="USD">USD</MenuItem>
                        <MenuItem value="EUR">EUR</MenuItem>
                        <MenuItem value="GBP">GBP</MenuItem>
                        <MenuItem value="CAD">CAD</MenuItem>
                      </TextField>
                    </Box>

                    {/* Fee Calculation Display */}
                    {form.ticket_price && parseFloat(form.ticket_price) > 0 && (
                      <Box sx={{ mb: 2, p: 2, bgcolor: TRANSPARENT_PINK_05, borderRadius: 1, border: `1px solid ${TRANSPARENT_PINK_3}` }}>
                        <Typography variant="body2" fontWeight="bold" sx={{ color: PRIMARY_PINK, mb: 1 }}>
                          💰 Fee Breakdown
                        </Typography>
                        {(() => {
                          const fee = calculateFee(parseFloat(form.ticket_price));
                          return (
                            <Box>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                                <strong>Platform Fee:</strong> ${fee.totalFee.toFixed(2)} (8%)
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                                <strong>Club Receives:</strong> ${fee.clubReceives.toFixed(2)} (92%)
                              </Typography>
                            </Box>
                          );
                        })()}
                      </Box>
                    )}

                    <TextField
                      margin="dense"
                      fullWidth
                      id="ticket_quantity"
                      label="Available Tickets"
                      name="ticket_quantity"
                      type="number"
                      value={form.ticket_quantity}
                      onChange={handleChange}
                      placeholder="Leave empty for unlimited"
                      inputProps={{ min: 1 }}
                      sx={{ mb: 2 }}
                    />

                    <TextField
                      margin="dense"
                      fullWidth
                      id="ticket_description"
                      label="Ticket Description"
                      name="ticket_description"
                      multiline
                      rows={2}
                      value={form.ticket_description}
                      onChange={handleChange}
                      placeholder="What's included with this ticket?"
                      sx={{ mb: 2 }}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Registration Section */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: PRIMARY_PINK }}>
                  Registration Settings
                </Typography>
                
                <TextField
                  margin="dense"
                  fullWidth
                  id="registration_url"
                  label="Registration Link"
                  name="registration_url"
                  value={form.registration_url}
                  onChange={handleChange}
                  placeholder="https://example.com/register"
                  sx={{ mb: 2 }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={form.startgg_event}
                      onChange={handleSwitchChange("startgg_event")}
                      name="startgg_event"
                      sx={{
                        '& .MuiSwitch-root': {
                          '& .MuiSwitch-switchBase': {
                            color: form.startgg_event ? PRIMARY_PINK : '#ccc',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: PRIMARY_PINK,
                            '&:hover': {
                              backgroundColor: TRANSPARENT_PINK_08,
                            },
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: PRIMARY_PINK,
                          },
                          '& .MuiSwitch-track': {
                            backgroundColor: form.startgg_event ? PRIMARY_PINK : '#ccc',
                          },
                          '& .MuiSwitch-switchBase.Mui-disabled': {
                            color: '#ccc !important',
                          },
                          '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
                            backgroundColor: '#ccc !important',
                          },
                        },
                      }}
                    />
                  }
                  label="Start.gg Event"
                  sx={{ mb: 2 }}
                />

                {form.startgg_event && (
                  <Box sx={{ ml: 2, mb: 2 }}>
                    <TextField
                      margin="dense"
                      required
                      fullWidth
                      id="slug"
                      label="Slug"
                      name="slug"
                      value={form.slug}
                      onChange={handleChange}
                      placeholder="tournament/tournament-name/event/event-name"
                      helperText="Format: tournament/tournament-name/event/event-name (from start.gg URL)"
                      sx={{ mb: 2 }}
                    />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.embed_registration}
                          onChange={handleSwitchChange("embed_registration")}
                          name="embed_registration"
                                                   sx={{
                           '& .MuiSwitch-root': {
                             '& .MuiSwitch-switchBase': {
                               color: form.embed_registration ? PRIMARY_PINK : '#ccc',
                             },
                             '& .MuiSwitch-switchBase.Mui-checked': {
                               color: PRIMARY_PINK,
                               '&:hover': {
                                 backgroundColor: TRANSPARENT_PINK_08,
                               },
                             },
                             '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                               backgroundColor: PRIMARY_PINK,
                             },
                             '& .MuiSwitch-track': {
                               backgroundColor: form.embed_registration ? PRIMARY_PINK : '#ccc',
                             },
                             '& .MuiSwitch-switchBase.Mui-disabled': {
                               color: '#ccc !important',
                             },
                             '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
                               backgroundColor: '#ccc !important',
                             },
                           },
                         }}
                        />
                      }
                      label="Embed Registration"
                      sx={{ mb: 2 }}
                    />

                    {form.embed_registration && (
                      <TextField
                        margin="dense"
                        required
                        fullWidth
                        id="iframe_data"
                        label="Iframe Data"
                        name="iframe_data"
                        multiline
                        rows={3}
                        value={form.iframe_data}
                        onChange={handleChange}
                        placeholder="<iframe src='...'></iframe>"
                        sx={{ mb: 2 }}
                      />
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Privacy & Visibility Section */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: PRIMARY_PINK }}>
                  Privacy & Visibility Settings
                </Typography>
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.private_event}
                      onChange={handleSwitchChange("private_event")}
                      name="private_event"
                      disabled={form.ticket_available}
                      sx={{
                        '& .MuiSwitch-root': {
                          '& .MuiSwitch-switchBase': {
                            color: form.private_event ? PRIMARY_PINK : '#ccc',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: PRIMARY_PINK,
                            '&:hover': {
                              backgroundColor: TRANSPARENT_PINK_08,
                            },
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: PRIMARY_PINK,
                          },
                          '& .MuiSwitch-track': {
                            backgroundColor: form.private_event ? PRIMARY_PINK : '#ccc',
                          },
                          '& .MuiSwitch-switchBase.Mui-disabled': {
                            color: '#ccc !important',
                          },
                          '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
                            backgroundColor: '#ccc !important',
                          },
                        },
                      }}
                    />
                  }
                  label="Private Event (Users must apply to join)"
                  sx={{ mb: 2 }}
                />
                
                {form.ticket_available && (
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 2, fontStyle: 'italic' }}>
                    Private events are not available for ticketed events. Users need to see event details to purchase tickets.
                  </Typography>
                )}
                
                                 <FormControlLabel
                   control={
                     <Switch
                       checked={form.hide_location_until_rsvp}
                       onChange={handleSwitchChange("hide_location_until_rsvp")}
                       name="hide_location_until_rsvp"
                       disabled={form.ticket_available}
                       sx={{
                         '& .MuiSwitch-root': {
                           '& .MuiSwitch-switchBase': {
                             color: form.hide_location_until_rsvp ? PRIMARY_PINK : '#ccc',
                           },
                           '& .MuiSwitch-switchBase.Mui-checked': {
                             color: PRIMARY_PINK,
                             '&:hover': {
                               backgroundColor: TRANSPARENT_PINK_08,
                             },
                           },
                           '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                             backgroundColor: PRIMARY_PINK,
                           },
                           '& .MuiSwitch-track': {
                             backgroundColor: form.hide_location_until_rsvp ? PRIMARY_PINK : '#ccc',
                           },
                           '& .MuiSwitch-switchBase.Mui-disabled': {
                             color: '#ccc !important',
                           },
                           '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
                             backgroundColor: '#ccc !important',
                           },
                         },
                       }}
                     />
                   }
                   label="Hide Event Location Until RSVP'd"
                   sx={{ mb: 2 }}
                 />
                 
                 {form.ticket_available && (
                   <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 2, fontStyle: 'italic' }}>
                     Location hiding is not available for ticketed events. Users need to see the location to purchase tickets.
                   </Typography>
                 )}
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.hide_rsvp_list}
                      onChange={handleSwitchChange("hide_rsvp_list")}
                                             name="hide_rsvp_list"
                       sx={{
                        '& .MuiSwitch-root': {
                          '& .MuiSwitch-switchBase': {
                            color: form.hide_rsvp_list ? PRIMARY_PINK : '#ccc',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: PRIMARY_PINK,
                            '&:hover': {
                              backgroundColor: TRANSPARENT_PINK_08,
                            },
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: PRIMARY_PINK,
                            },
                          '& .MuiSwitch-track': {
                            backgroundColor: form.hide_rsvp_list ? PRIMARY_PINK : '#ccc',
                          },
                          '& .MuiSwitch-switchBase.Mui-disabled': {
                            color: '#ccc !important',
                          },
                          '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
                            backgroundColor: '#ccc !important',
                          },
                        },
                      }}
                    />
                  }
                  label="Hide RSVP List from Public View"
                  sx={{ mb: 2 }}
                />
                

              </CardContent>
            </Card>

            {/* USCF Event Section */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: PRIMARY_PINK }}>
                  USCF Event Settings
                </Typography>
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.uscf_event}
                      onChange={handleSwitchChange("uscf_event")}
                      name="uscf_event"
                      sx={{
                        '& .MuiSwitch-root': {
                          '& .MuiSwitch-switchBase': {
                            color: form.uscf_event ? PRIMARY_PINK : '#ccc',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: PRIMARY_PINK,
                            '&:hover': {
                              backgroundColor: TRANSPARENT_PINK_08,
                            },
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: PRIMARY_PINK,
                          },
                          '& .MuiSwitch-track': {
                            backgroundColor: form.uscf_event ? PRIMARY_PINK : '#ccc',
                          },
                        },
                      }}
                    />
                  }
                  label="Enable USCF Event Registration"
                  sx={{ mb: 2 }}
                />
                
                {form.uscf_event && (
                  <Box sx={{ ml: 2, mb: 2 }}>
                    <TextField
                      margin="dense"
                      fullWidth
                      id="uscf_ticket_price"
                      label="USCF Event Price (Optional)"
                      name="uscf_ticket_price"
                      type="number"
                      value={form.uscf_ticket_price}
                      onChange={handleChange}
                      placeholder="0.00"
                      inputProps={{ min: 0, step: 0.01 }}
                      helperText="Leave empty for free USCF event"
                      sx={{ mb: 2 }}
                    />
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      This enables USCF member registration with section management. 
                      Configure sections and rating requirements below.
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* USCF Sections Management - Only show for USCF events */}
            {form.uscf_event && (
              <USCFSectionsManager eventId={eventId} />
            )}

            {/* RSVPs List Section - Only show if RSVP is enabled */}
            {form.rsvp_enabled && (
              <Card sx={{ mb: 3, mt: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box>
                      <Typography variant="h5" gutterBottom sx={{ color: PRIMARY_PINK }}>
                        <GroupIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
                        RSVPs
                        {rsvps.length > 0 && (
                          <Chip
                            label={rsvps.length}
                            size="small"
                            sx={{
                              ml: 2,
                              bgcolor: SUCCESS_GREEN,
                              color: 'white',
                              fontWeight: 'bold'
                            }}
                          />
                        )}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary">
                        View and manage RSVPs for this event.
                        {event?.rsvp_limit && (
                          <span>
                            {' '}({rsvps.length} / {event.rsvp_limit})
                          </span>
                        )}
                      </Typography>
                    </Box>
                    
                    <MKButton
                      variant="outlined"
                      onClick={fetchRSVPs}
                      disabled={rsvpsLoading}
                      startIcon={rsvpsLoading ? <CircularProgress size={20} /> : <GroupIcon />}
                      sx={{
                        color: darkMode ? '#ffffff' : '#000000',
                        borderColor: darkMode ? '#ffffff' : '#000000',
                        '&:hover': {
                          borderColor: darkMode ? '#ffffff' : '#000000',
                          backgroundColor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
                        }
                      }}
                    >
                      Refresh
                    </MKButton>
                  </Box>

                  {rsvpsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : rsvps.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No RSVPs yet
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                        <MKButton
                          variant="contained"
                          onClick={exportToCSV}
                          disabled={exportingCSV}
                          startIcon={exportingCSV ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                          sx={{
                            bgcolor: PRIMARY_PINK,
                            color: '#ffffff',
                            '&:hover': {
                              bgcolor: PRIMARY_PINK_DARK
                            }
                          }}
                        >
                          {exportingCSV ? 'Exporting...' : 'Export to CSV'}
                        </MKButton>
                      </Box>

                      <Box sx={{ maxHeight: 600, overflowY: 'auto' }}>
                        {rsvps.map((rsvp, index) => (
                          <Box
                            key={rsvp.id}
                            sx={{
                              p: 2,
                              mb: 2,
                              borderRadius: 1,
                              border: `1px solid ${BORDER_LIGHT}`,
                              bgcolor: index % 2 === 0 ? TRANSPARENT_PINK_05 : 'transparent',
                              '&:hover': {
                                bgcolor: TRANSPARENT_PINK_1,
                              },
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="h6" sx={{ mb: 0.5 }}>
                                  {rsvp.display_name || rsvp.email}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {rsvp.email}
                                </Typography>
                              </Box>
                              <Chip
                                label={rsvp.attendee_status || 'confirmed'}
                                size="small"
                                sx={{
                                  bgcolor: rsvp.attendee_status === 'confirmed' ? SUCCESS_GREEN : WARNING_ORANGE,
                                  color: 'white',
                                  fontWeight: 'bold'
                                }}
                              />
                            </Box>
                            
                            {event?.chess_event && rsvp.chess_registration && (
                              <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${BORDER_LIGHT}` }}>
                                <Typography variant="subtitle2" sx={{ mb: 1, color: PRIMARY_PINK, fontWeight: 'bold' }}>
                                  Chess Registration Information:
                                </Typography>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} sm={6}>
                                    <Typography variant="body2">
                                      <strong>First Name:</strong> {rsvp.chess_registration.first_name}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <Typography variant="body2">
                                      <strong>Last Name:</strong> {rsvp.chess_registration.last_name}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <Typography variant="body2">
                                      <strong>Rating:</strong> {rsvp.chess_registration.rating}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <Typography variant="body2">
                                      <strong>Rating Provider:</strong> {rsvp.chess_registration.rating_provider}
                                    </Typography>
                                  </Grid>
                                  {rsvp.chess_registration.uscf_id && (
                                    <Grid item xs={12} sm={6}>
                                      <Typography variant="body2">
                                        <strong>USCF ID:</strong> {rsvp.chess_registration.uscf_id}
                                      </Typography>
                                    </Grid>
                                  )}
                                </Grid>
                              </Box>
                            )}

                            {event?.dnd_event && rsvp.dnd_registration && (
                              <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${BORDER_LIGHT}` }}>
                                <Typography variant="subtitle2" sx={{ mb: 1, color: PRIMARY_PINK, fontWeight: 'bold' }}>
                                  DnD Registration Information:
                                </Typography>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} sm={6}>
                                    <Typography variant="body2">
                                      <strong>Campaigns:</strong> {rsvp.dnd_registration.campaigns}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <Typography variant="body2">
                                      <strong>Favorite Classes:</strong> {rsvp.dnd_registration.favorite_classes}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <Typography variant="body2">
                                      <strong>Experience Level:</strong> {rsvp.dnd_registration.experience_level}
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Box>
                            )}
                            
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                              RSVP Date: {new Date(rsvp.created_at).toLocaleString()}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Tickets List Section - Only show if ticket sales are enabled */}
            {form.ticket_available && (
              <Card sx={{ mb: 3, mt: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box>
                      <Typography variant="h5" gutterBottom sx={{ color: PRIMARY_PINK }}>
                        <EventIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
                        Ticket Purchases
                        {tickets.length > 0 && (
                          <Chip
                            label={tickets.length}
                            size="small"
                            sx={{
                              ml: 2,
                              bgcolor: SUCCESS_GREEN,
                              color: 'white',
                              fontWeight: 'bold'
                            }}
                          />
                        )}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary">
                        View and manage ticket purchases for this event.
                        {event?.ticket_quantity && (
                          <span>
                            {' '}({tickets.reduce((sum, t) => sum + (t.ticket_quantity || 1), 0)} / {event.ticket_quantity} tickets sold)
                          </span>
                        )}
                      </Typography>
                    </Box>
                    
                    <MKButton
                      variant="outlined"
                      onClick={fetchTickets}
                      disabled={ticketsLoading}
                      startIcon={ticketsLoading ? <CircularProgress size={20} /> : <EventIcon />}
                      sx={{
                        color: darkMode ? '#ffffff' : '#000000',
                        borderColor: darkMode ? '#ffffff' : '#000000',
                        '&:hover': {
                          borderColor: darkMode ? '#ffffff' : '#000000',
                          backgroundColor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
                        }
                      }}
                    >
                      Refresh
                    </MKButton>
                  </Box>

                  {ticketsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : tickets.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No tickets purchased yet
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                        <MKButton
                          variant="contained"
                          onClick={exportTicketsToCSV}
                          disabled={exportingCSV}
                          startIcon={exportingCSV ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                          sx={{
                            bgcolor: PRIMARY_PINK,
                            color: '#ffffff',
                            '&:hover': {
                              bgcolor: PRIMARY_PINK_DARK
                            }
                          }}
                        >
                          {exportingCSV ? 'Exporting...' : 'Export to CSV'}
                        </MKButton>
                      </Box>

                      <Box sx={{ maxHeight: 600, overflowY: 'auto' }}>
                        {tickets.map((ticket, index) => (
                          <Box
                            key={ticket.id}
                            sx={{
                              p: 2,
                              mb: 2,
                              borderRadius: 1,
                              border: `1px solid ${BORDER_LIGHT}`,
                              bgcolor: index % 2 === 0 ? TRANSPARENT_PINK_05 : 'transparent',
                              '&:hover': {
                                bgcolor: TRANSPARENT_PINK_1,
                              },
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="h6" sx={{ mb: 0.5 }}>
                                  {ticket.display_name || ticket.email}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {ticket.email}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                  {ticket.ticket_quantity || 1} ticket{ticket.ticket_quantity > 1 ? 's' : ''} • ${ticket.ticket_price || '0.00'} {ticket.ticket_currency || 'USD'}
                                </Typography>
                                {ticket.confirmation_code && (
                                  <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 'bold', color: PRIMARY_PINK }}>
                                    Confirmation Code: {ticket.confirmation_code}
                                  </Typography>
                                )}
                              </Box>
                              <Chip
                                label={ticket.attendee_status || 'confirmed'}
                                size="small"
                                sx={{
                                  bgcolor: ticket.attendee_status === 'confirmed' ? SUCCESS_GREEN : WARNING_ORANGE,
                                  color: 'white',
                                  fontWeight: 'bold'
                                }}
                              />
                            </Box>
                            
                            {event?.chess_event && ticket.chess_registration && (
                              <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${BORDER_LIGHT}` }}>
                                <Typography variant="subtitle2" sx={{ mb: 1, color: PRIMARY_PINK, fontWeight: 'bold' }}>
                                  Chess Registration Information:
                                </Typography>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} sm={6}>
                                    <Typography variant="body2">
                                      <strong>First Name:</strong> {ticket.chess_registration.first_name}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <Typography variant="body2">
                                      <strong>Last Name:</strong> {ticket.chess_registration.last_name}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <Typography variant="body2">
                                      <strong>Rating:</strong> {ticket.chess_registration.rating}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <Typography variant="body2">
                                      <strong>Rating Provider:</strong> {ticket.chess_registration.rating_provider}
                                    </Typography>
                                  </Grid>
                                  {ticket.chess_registration.uscf_id && (
                                    <Grid item xs={12} sm={6}>
                                      <Typography variant="body2">
                                        <strong>USCF ID:</strong> {ticket.chess_registration.uscf_id}
                                      </Typography>
                                    </Grid>
                                  )}
                                </Grid>
                              </Box>
                            )}

                            {event?.dnd_event && ticket.dnd_registration && (
                              <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${BORDER_LIGHT}` }}>
                                <Typography variant="subtitle2" sx={{ mb: 1, color: PRIMARY_PINK, fontWeight: 'bold' }}>
                                  DnD Registration Information:
                                </Typography>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} sm={6}>
                                    <Typography variant="body2">
                                      <strong>Campaigns:</strong> {ticket.dnd_registration.campaigns}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <Typography variant="body2">
                                      <strong>Favorite Classes:</strong> {ticket.dnd_registration.favorite_classes}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <Typography variant="body2">
                                      <strong>Experience Level:</strong> {ticket.dnd_registration.experience_level}
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Box>
                            )}
                            
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                              Purchase Date: {new Date(ticket.created_at).toLocaleString()}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>

         {/* Event Requests Section - Only show for private events */}
         {event?.private_event && (
           <Card sx={{ mb: 3, mt: 3 }}>
             <CardContent>
               <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                 <Box>
                   <Typography variant="h5" gutterBottom sx={{ color: PRIMARY_PINK }}>
                     <GroupIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
                     Event Join Requests
                     {eventRequests.length > 0 && (
                       <Chip
                         label={eventRequests.length}
                         size="small"
                         sx={{
                           ml: 2,
                           bgcolor: eventRequests.some(r => r.status === 'pending') ? WARNING_ORANGE : SUCCESS_GREEN,
                           color: 'white',
                           fontWeight: 'bold'
                         }}
                       />
                     )}
                   </Typography>
                   
                   <Typography variant="body2" color="text.secondary">
                     Review and manage requests from users who want to join this private event.
                     {eventRequests.some(r => r.status === 'pending') && (
                       <span style={{ color: WARNING_ORANGE, fontWeight: 'bold' }}>
                         {' '}({eventRequests.filter(r => r.status === 'pending').length} pending)
                       </span>
                     )}
                   </Typography>
                 </Box>
                 
                                   <MKButton
                    variant="outlined"
                    onClick={() => fetchEventRequests(event.id)}
                    disabled={requestsLoading}
                    startIcon={requestsLoading ? <CircularProgress size={20} /> : <GroupIcon />}
                    sx={{
                      color: darkMode ? '#ffffff' : '#000000',
                      borderColor: darkMode ? '#ffffff' : '#000000',
                      '&:hover': {
                        borderColor: darkMode ? '#ffffff' : '#000000',
                        backgroundColor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
                      }
                    }}
                  >
                    Refresh
                  </MKButton>
               </Box>

               {requestsLoading ? (
                 <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                   <CircularProgress />
                 </Box>
               ) : eventRequests.length === 0 ? (
                 <Box sx={{ textAlign: 'center', p: 3 }}>
                   <Typography variant="body2" color="text.secondary">
                     No pending requests at this time.
                   </Typography>
                 </Box>
                               ) : (
                                     <Box sx={{ width: '100%', ...dataGridStyles }}>
                                           <DataGrid
                        rows={currentRequests}
                        columns={eventRequestColumns}
                        getRowId={(row) => row.id}
                        loading={requestsLoading}
                        pageSize={rowsPerPage}
                        rowsPerPageOptions={[5, 10, 25]}
                        disableSelectionOnClick
                        hideFooterPagination={true}
                        sx={{
                          ...dataGridStyles,
                          minHeight: 400,
                          '& .MuiDataGrid-main': {
                            minHeight: 400,
                          },
                          '& .MuiDataGrid-virtualScroller': {
                            minHeight: 400,
                          },
                          '& .MuiDataGrid-row': {
                            '&:hover': {
                              backgroundColor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                            },
                          },
                          '& .MuiDataGrid-cell': {
                            display: 'flex',
                            alignItems: 'center',
                            padding: '8px 16px',
                          },

                        }}
                        componentsProps={{
                          filterPanel: {
                            sx: {
                              backgroundColor: darkMode ? '#1e1e1e' : '#ffffff',
                              color: darkMode ? '#ffffff' : '#000000',
                            },
                          },
                        }}
                      />
                     
                     {/* Custom Pagination Controls */}
                     {eventRequests.length > 0 && (
                       <Box sx={{ 
                         display: 'flex', 
                         justifyContent: 'space-between', 
                         alignItems: 'center', 
                         mt: 2, 
                         p: 2,
                         borderTop: `1px solid ${BORDER_LIGHT}`,
                         bgcolor: 'background.paper'
                       }}>
                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                           <Typography variant="body2" color="text.secondary">
                             Rows per page:
                           </Typography>
                           <Select
                             value={rowsPerPage}
                             onChange={handleRowsPerPageChange}
                             size="small"
                             sx={{ minWidth: 80 }}
                           >
                             <MenuItem value={5}>5</MenuItem>
                             <MenuItem value={10}>10</MenuItem>
                             <MenuItem value={25}>25</MenuItem>
                           </Select>
                           <Typography variant="body2" color="text.secondary">
                             {startIndex + 1}-{Math.min(endIndex, eventRequests.length)} of {eventRequests.length}
                           </Typography>
                         </Box>
                         
                         <Pagination
                           count={totalPages}
                           page={currentPage}
                           onChange={handlePageChange}
                           color="primary"
                           size="medium"
                           showFirstButton
                           showLastButton
                           sx={{
                             '& .MuiPaginationItem-root': {
                               color: darkMode ? '#ffffff' : '#000000',
                             },
                             '& .MuiPaginationItem-root.Mui-selected': {
                               bgcolor: PRIMARY_PINK,
                               color: '#ffffff',
                               '&:hover': {
                                 bgcolor: PRIMARY_PINK_DARK,
                               },
                             },
                           }}
                         />
                       </Box>
                     )}
                   </Box>
                )}
             </CardContent>
           </Card>
         )}

                 {/* Action Buttons */}
         <Box sx={{ display: 'flex', gap: 2, mt: 4, justifyContent: 'flex-end' }}>
           {event?.uscf_event && (
             <>
               <MKButton
                 variant="outlined"
                 startIcon={<QrCodeIcon />}
                 onClick={handleGenerateQRCode}
                 sx={{
                   borderColor: PRIMARY_PINK,
                   color: PRIMARY_PINK,
                   "&:hover": {
                     borderColor: PRIMARY_PINK_DARK,
                     color: PRIMARY_PINK_DARK,
                   },
                 }}
               >
                 Generate QR Code
               </MKButton>
               
               <MKButton
                 variant="outlined"
                 startIcon={exportingCSV ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                 onClick={handleExportSwissSys}
                 disabled={exportingCSV}
                 sx={{
                   borderColor: PRIMARY_PINK,
                   color: PRIMARY_PINK,
                   "&:hover": {
                     borderColor: PRIMARY_PINK_DARK,
                     color: PRIMARY_PINK_DARK,
                   },
                 }}
               >
                 {exportingCSV ? "Exporting..." : "Export SwissSys CSV"}
               </MKButton>
             </>
           )}
           
           <MKButton
             variant="contained"
             color="error"
             startIcon={<DeleteIcon />}
             onClick={handleDelete}
             disabled={saving || deleting}
           >
             {deleting ? "Deleting..." : "Delete Event"}
           </MKButton>
           
           <MKButton
             variant="contained"
             type="submit"
             startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
             disabled={saving || deleting}
             sx={{
               bgcolor: PRIMARY_PINK,
               color: '#ffffff',
               "&:hover": {
                 bgcolor: PRIMARY_PINK_DARK,
               },
             }}
           >
             {saving ? "Saving..." : "Save Changes"}
           </MKButton>
         </Box>
      </Box>

      {/* QR Code Dialog */}
      <Dialog open={showQrDialog} onClose={() => setShowQrDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" component="div">
            USCF Registration QR Code
          </Typography>
        </DialogTitle>
        <DialogContent>
          {qrCodeData && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {qrCodeData.event_title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {new Date(qrCodeData.event_date).toLocaleDateString()}
              </Typography>
              
              {/* QR Code will be generated here using a QR code library */}
              <Box sx={{ 
                p: 3, 
                border: '2px dashed #ccc', 
                borderRadius: 2, 
                mb: 2,
                minHeight: 200,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Typography variant="body2" color="text.secondary">
                  QR Code for: {qrCodeData.qr_code_url}
                </Typography>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Scan this QR code to register for the USCF event
              </Typography>
              
              <TextField
                fullWidth
                label="Registration URL"
                value={qrCodeData.qr_code_url}
                InputProps={{
                  readOnly: true,
                }}
                sx={{ mb: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowQrDialog(false)}>
            Close
          </Button>
          <MKButton
            variant="contained"
            onClick={() => {
              navigator.clipboard.writeText(qrCodeData?.qr_code_url);
              setSuccess('URL copied to clipboard!');
            }}
            sx={{
              bgcolor: PRIMARY_PINK,
              color: '#ffffff',
              '&:hover': {
                bgcolor: PRIMARY_PINK_DARK,
              },
            }}
          >
            Copy URL
          </MKButton>
        </DialogActions>
      </Dialog>
    </Container>
    </LocalizationProvider>
  );
}

export default EditEventPage;
